import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import apiClient from "../../../services/apiClient";
import { readSessionFromUrl } from "../../leveltest/session";
import "./WrongNotePage.css";

// 수학 오답 올리기(2026-10-06). 서버: api/assignment/math_wrong_note.py
// 틀린 문제 사진·PDF 를 올리면 담당 선생님의 지인에듀 수학 오답노트로 간다. 선생님이 쌍둥이 문제를 만들어
// 내 주면 '미완료 과제' 에 과제로 들어온다. 수학 학원 학생에게만 메뉴가 보인다(HomePage).

const STATUS_TEXT = { sent: "선생님 확인 중", assigned: "과제 나옴", failed: "다시 올려 주세요" };
const ACCEPT = "image/jpeg,image/png,image/webp,image/heic,.heic,application/pdf";
const MAX_FILE_COUNT = 50;

const formatDate = (iso) => {
  const date = new Date(iso);
  return `${date.getMonth() + 1}/${date.getDate()}`;
};

const WrongNotePage = () => {
  const identity = useMemo(() => readSessionFromUrl(), []);
  const inputRef = useRef(null);
  const [fileList, setFileList] = useState([]);
  const [noteList, setNoteList] = useState([]);
  // 하루에 한 번만 올린다(서버가 정한다)
  const [canUploadToday, setCanUploadToday] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(() => {
    if (!identity) return;
    apiClient
      .get("/api/v1/assignment/math/wrong-note", {
        params: { student_id: identity.studentId, random_code: identity.randomCode },
      })
      .then((response) => {
        setNoteList(response.data.note_list);
        setCanUploadToday(response.data.can_upload_today !== false);
      })
      .catch(() => {});
  }, [identity]);

  useEffect(load, [load]);

  const previewList = useMemo(
    () => fileList.map((file) => ({ file, url: file.type.startsWith("image/") ? URL.createObjectURL(file) : "" })),
    [fileList]
  );
  useEffect(() => () => previewList.forEach((one) => one.url && URL.revokeObjectURL(one.url)), [previewList]);

  const addFiles = (picked) => {
    const merged = [...fileList, ...Array.from(picked)];
    setMessage(merged.length > MAX_FILE_COUNT ? `한 번에 ${MAX_FILE_COUNT}개까지예요. 앞의 ${MAX_FILE_COUNT}개만 담았어요.` : "");
    setFileList(merged.slice(0, MAX_FILE_COUNT));
  };

  const send = async () => {
    if (!identity || fileList.length === 0 || isSending) return;
    setIsSending(true);
    setMessage("");
    const form = new FormData();
    form.append("student_id", identity.studentId);
    form.append("random_code", identity.randomCode);
    fileList.forEach((file) => form.append("files", file));
    try {
      await apiClient.post("/api/v1/assignment/math/wrong-note", form);
      setFileList([]);
      setMessage("선생님께 보냈어요.");
      load();
    } catch (error) {
      setMessage(error?.response?.data?.error || "보내지 못했어요. 다시 눌러 주세요.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="wrong-note">
      <div className="wrong-note__title">틀린 문제 올리기</div>

      {canUploadToday ? (
        <>
          <div
            className="wrong-note__drop"
            onClick={() => inputRef.current?.click()}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              addFiles(event.dataTransfer.files);
            }}
          >
            <svg className="wrong-note__drop-icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 8h3l2-2.5h6L17 8h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
              <circle cx="12" cy="13.5" r="3.6" />
            </svg>
            <b>사진 · PDF 고르기</b>
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              multiple
              hidden
              onChange={(event) => {
                addFiles(event.target.files);
                event.target.value = "";
              }}
            />
          </div>

          {previewList.length > 0 && (
            <div className="wrong-note__previews">
              {previewList.map((one, index) => (
                <div key={`${one.file.name}-${index}`} className="wrong-note__preview">
                  {one.url ? <img src={one.url} alt="" /> : <span className="wrong-note__pdf">PDF</span>}
                  <button
                    type="button"
                    aria-label="빼기"
                    onClick={() => setFileList((previous) => previous.filter((_, fileIndex) => fileIndex !== index))}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="wrong-note__actions">
            {message && <span className="wrong-note__message">{message}</span>}
            <button type="button" className="wrong-note__send" disabled={fileList.length === 0 || isSending} onClick={send}>
              {isSending ? "보내는 중" : fileList.length ? `${fileList.length}개 보내기` : "보내기"}
            </button>
          </div>
        </>
      ) : (
        <div className="wrong-note__done">오늘은 올렸어요. 내일 또 올릴 수 있어요.</div>
      )}

      {noteList.length > 0 && (
        <div className="wrong-note__history">
          <div className="wrong-note__history-title">올린 기록</div>
          {noteList.map((note) => (
            <div key={note.id} className="wrong-note__row">
              <span className="wrong-note__row-date">{formatDate(note.created_at)}</span>
              <span className="wrong-note__row-title">{note.title}</span>
              <span className={`wrong-note__status wrong-note__status--${note.status}`}>
                {STATUS_TEXT[note.status] || note.status}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WrongNotePage;
